import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Timer, Check, House, Bell, ArrowsClockwise, Fire, Printer,
} from "@phosphor-icons/react";
import { api, authHeaders } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import printReceipt from "@/lib/printReceipt";
import { getState as getBtState, printOrder as btPrintOrder } from "@/lib/btThermal";

/**
 * Kitchen Display System (KDS) — full-screen view designed for a tablet or
 * monitor in the kitchen. Shows only paid orders that haven't been delivered
 * yet, sorted oldest first. Cards turn yellow after 5 min and red after 10 min
 * so the staff knows which ticket to grab next.
 */

const REFRESH_MS = 6000;
const ACTIVE_STATUSES = ["preparing", "ready"];

function elapsedSeconds(iso) {
    if (!iso) return 0;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return 0;
    return Math.max(0, Math.floor((Date.now() - t) / 1000));
}

function formatElapsed(s) {
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${m}:${ss.toString().padStart(2, "0")}`;
}

function urgencyLevel(secs) {
    if (secs >= 600) return "red";    // > 10 min
    if (secs >= 300) return "yellow"; // > 5 min
    return "green";
}

function playBeep(kind = "new") {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const now = ctx.currentTime;
        const beeps =
            kind === "new"
                ? [{ f: 880, d: 0.16 }, { f: 988, d: 0.16 }, { f: 1175, d: 0.3 }]
                : [{ f: 1175, d: 0.2 }];
        let t = 0;
        beeps.forEach((b) => {
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.type = "sine";
            o.frequency.setValueAtTime(b.f, now + t);
            g.gain.setValueAtTime(0.0001, now + t);
            g.gain.exponentialRampToValueAtTime(0.4, now + t + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, now + t + b.d);
            o.start(now + t);
            o.stop(now + t + b.d + 0.05);
            t += b.d + 0.04;
        });
    } catch { /* noop */ }
}

export default function Kitchen() {
    const { user, token, logout } = useAdmin();
    const [orders, setOrders] = useState([]);
    const [tick, setTick] = useState(0);
    const seenRef = useRef(new Set());
    const initialized = useRef(false);
    const [soundOn, setSoundOn] = useState(
        () => localStorage.getItem("jw_kitchen_sound") !== "off",
    );

    // Fetch every 6s + bump tick every 1s so timers update.
    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const r = await api.get("/orders", { headers: authHeaders() });
                if (!alive) return;
                // Only paid (or in-store cash/card) active orders.
                const active = (r.data || []).filter((o) => {
                    const paidish = o.payment_status === "paid" || o.payment_method === "cash" || o.payment_method === "card";
                    return paidish && ACTIVE_STATUSES.includes(o.status);
                });
                // Detect newcomers
                if (!initialized.current) {
                    active.forEach((o) => seenRef.current.add(o.id));
                    initialized.current = true;
                } else {
                    const fresh = active.filter((o) => !seenRef.current.has(o.id));
                    if (fresh.length > 0) {
                        fresh.forEach((o) => seenRef.current.add(o.id));
                        if (soundOn) playBeep("new");
                    }
                }
                setOrders(active.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")));
            } catch {
                // ignore
            }
        };
        load();
        const id = setInterval(load, REFRESH_MS);
        const t = setInterval(() => setTick((x) => x + 1), 1000);
        return () => { alive = false; clearInterval(id); clearInterval(t); };
    }, [soundOn]);

    const markReady = async (id) => {
        try {
            await api.put(`/orders/${id}/status`, { status: "ready" }, { headers: authHeaders() });
            if (soundOn) playBeep("ready");
            toast.success("Orden lista · avisa al frente");
            // Optimistic: update local state
            setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status: "ready" } : o)));
        } catch {
            toast.error("No se pudo actualizar");
        }
    };

    const markDelivered = async (id) => {
        try {
            await api.put(`/orders/${id}/status`, { status: "delivered" }, { headers: authHeaders() });
            toast.success("Entregada");
            setOrders((prev) => prev.filter((o) => o.id !== id));
        } catch {
            toast.error("No se pudo actualizar");
        }
    };

    const toggleSound = () => {
        setSoundOn((s) => {
            const next = !s;
            localStorage.setItem("jw_kitchen_sound", next ? "on" : "off");
            toast(next ? "Sonido ON" : "Sonido OFF");
            if (next) playBeep("new");
            return next;
        });
    };

    const stats = useMemo(() => {
        const preparing = orders.filter((o) => o.status === "preparing").length;
        const ready = orders.filter((o) => o.status === "ready").length;
        return { preparing, ready };
    }, [orders]);

    if (!token || !user) return <Navigate to="/admin/login" replace />;

    return (
        <div data-testid="kitchen-view" className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col">
            {/* Top bar */}
            <header className="bg-zinc-900 border-b border-zinc-800 px-4 md:px-8 py-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3">
                    <Fire size={32} weight="fill" className="text-orange-500" />
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                            J & W Deli
                        </p>
                        <h1 className="font-display font-black text-2xl leading-none">
                            Vista de Cocina
                        </h1>
                    </div>
                </div>

                <div className="ml-auto flex flex-wrap items-center gap-2 md:gap-4">
                    <Stat color="amber" label="Preparando" value={stats.preparing} />
                    <Stat color="emerald" label="Listas" value={stats.ready} />

                    <button
                        data-testid="kitchen-sound"
                        onClick={toggleSound}
                        className={`hidden sm:inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-widest border-2 ${
                            soundOn
                                ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                                : "border-zinc-700 bg-zinc-800 text-zinc-400"
                        }`}
                    >
                        <Bell size={14} weight="bold" />
                        {soundOn ? "ON" : "OFF"}
                    </button>

                    <Link
                        to="/admin"
                        data-testid="kitchen-back"
                        className="inline-flex items-center gap-2 rounded-full bg-zinc-800 border border-zinc-700 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-700"
                    >
                        <House size={14} weight="bold" /> Admin
                    </Link>
                    <button
                        data-testid="kitchen-signout"
                        onClick={logout}
                        className="hidden sm:inline-block text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
                    >
                        Salir
                    </button>
                </div>
            </header>

            {/* Grid */}
            <main className="flex-1 p-4 md:p-6">
                {orders.length === 0 ? (
                    <div className="h-full min-h-[50vh] grid place-items-center text-center">
                        <div>
                            <ArrowsClockwise size={48} weight="bold" className="text-zinc-700 mx-auto animate-spin-slow" />
                            <p className="font-display font-black text-3xl mt-4">
                                Sin órdenes pendientes
                            </p>
                            <p className="text-zinc-500 mt-2">
                                Cuando entre un pedido, aparecerá aquí con su temporizador.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {orders.map((o) => (
                            <OrderTicket
                                key={o.id + tick + (o.status || "")}
                                order={o}
                                onReady={() => markReady(o.id)}
                                onDelivered={() => markDelivered(o.id)}
                                onPrint={async () => {
                                    if (getBtState().connected) {
                                        try {
                                            await btPrintOrder(o, { kind: "kitchen" });
                                            return;
                                        } catch {
                                            // fall through to HTML print
                                        }
                                    }
                                    printReceipt(o, {
                                        kind: "kitchen",
                                        width: Number(localStorage.getItem("jw_printer_width")) || 58,
                                    });
                                }}
                            />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

function Stat({ color, label, value }) {
    const colors = {
        amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
        emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    };
    return (
        <div className={`px-3 py-2 rounded-xl border ${colors[color]}`}>
            <p className="text-[10px] uppercase tracking-widest opacity-80">{label}</p>
            <p className="font-display font-black text-2xl leading-none mt-0.5">{value}</p>
        </div>
    );
}

function OrderTicket({ order, onReady, onDelivered, onPrint }) {
    const elapsed = elapsedSeconds(order.created_at);
    const urgency = urgencyLevel(elapsed);
    const isReady = order.status === "ready";

    const tone = isReady
        ? {
              card: "bg-emerald-950/70 border-emerald-500",
              header: "bg-emerald-500 text-emerald-950",
              timer: "text-emerald-300",
          }
        : urgency === "red"
        ? {
              card: "bg-red-950/70 border-red-500 animate-pulse",
              header: "bg-red-500 text-red-950",
              timer: "text-red-300",
          }
        : urgency === "yellow"
        ? {
              card: "bg-amber-950/60 border-amber-500",
              header: "bg-amber-500 text-amber-950",
              timer: "text-amber-300",
          }
        : {
              card: "bg-zinc-900 border-zinc-700",
              header: "bg-zinc-100 text-zinc-900",
              timer: "text-zinc-300",
          };

    const channelLabel =
        order.channel === "pos"
            ? "POS · Mostrador"
            : order.fulfillment === "delivery"
            ? "Delivery"
            : "Pickup";

    return (
        <article
            data-testid={`kitchen-ticket-${order.id}`}
            className={`rounded-2xl border-2 overflow-hidden flex flex-col ${tone.card}`}
        >
            {/* Header */}
            <div className={`px-4 py-2 flex items-center justify-between ${tone.header}`}>
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">
                        #{(order.id || "").slice(-6).toUpperCase()}
                    </p>
                    <p className="font-display font-black text-lg leading-none mt-0.5 truncate max-w-[180px]">
                        {order.customer_name || "Walk-in"}
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">
                        {channelLabel}
                    </p>
                    <p className={`font-mono font-black text-2xl flex items-center gap-1 ${tone.timer} drop-shadow-sm`}>
                        <Timer size={18} weight="bold" />
                        {formatElapsed(elapsed)}
                    </p>
                </div>
            </div>

            {/* Items */}
            <ul className="px-4 py-3 space-y-2 flex-1">
                {(order.items || []).map((it, i) => (
                    <li key={i} className="flex items-baseline gap-3 border-b border-white/5 last:border-b-0 pb-2 last:pb-0">
                        <span className="font-display font-black text-2xl text-orange-400 leading-none w-7 shrink-0">
                            {it.qty}×
                        </span>
                        <span className="font-bold text-base leading-tight">
                            {it.name}
                        </span>
                    </li>
                ))}
            </ul>

            {/* Notes */}
            {order.notes && (
                <div className="px-4 pb-2">
                    <div className="bg-orange-500/15 border border-orange-500/40 rounded-lg px-3 py-2 text-sm">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-orange-400 mb-0.5">
                            Notas del cliente
                        </p>
                        <p className="text-orange-100 leading-snug">{order.notes}</p>
                    </div>
                </div>
            )}

            {/* Action */}
            <div className="px-4 pb-4 pt-2 flex items-center gap-2">
                {isReady ? (
                    <button
                        data-testid={`kitchen-delivered-${order.id}`}
                        onClick={onDelivered}
                        className="flex-1 h-14 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-display font-black text-lg uppercase tracking-widest flex items-center justify-center gap-2 transition"
                    >
                        <Check size={20} weight="bold" /> Entregada
                    </button>
                ) : (
                    <button
                        data-testid={`kitchen-ready-${order.id}`}
                        onClick={onReady}
                        className="flex-1 h-14 rounded-xl bg-white text-zinc-900 hover:bg-orange-500 hover:text-white font-display font-black text-lg uppercase tracking-widest flex items-center justify-center gap-2 transition"
                    >
                        <Check size={20} weight="bold" /> Lista
                    </button>
                )}
                <button
                    data-testid={`kitchen-print-${order.id}`}
                    onClick={onPrint}
                    aria-label="Imprimir ticket"
                    className="h-14 w-14 rounded-xl bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700 flex items-center justify-center transition shrink-0"
                >
                    <Printer size={20} weight="bold" />
                </button>
            </div>
        </article>
    );
}
